// ============================================================================
// ServiceLinesEditor — add, change and close out the service lines on a card.
//
// THE DEFECT THIS CLOSES. The card rendered its lines read-only under a
// sentence telling the reader to "edit the full set on the account card once
// the facility is live." No such editor existed anywhere. So a card created
// with one line could never gain a second, and an AM who needed a day porter
// alongside a nightly crew at the same Houston hospital did the only thing the
// software allowed: opened a second onboarding card for the same building.
//
// TWO SITUATIONS, ONE EDITOR.
//
//   BEFORE THE BUILDING EXISTS (no facility_id). Nothing downstream has been
//   created, so removal is literal — the line goes out of the jsonb and the
//   seeder later fans out exactly the lines the card ends with.
//
//   AFTER THE BUILDING IS LIVE (facility_id set). Every line here IS one of the
//   building's arrangements, read through the card's window and written back
//   through save_service_line() — the one door. ⛔ Nothing is ever deleted: a
//   line that has been invoiced must stay readable, so removing a live one is
//   STOPPING it, on a night somebody names.
//
// ⛔ THIS EDITOR NEVER WRITES A RATE ON A LINE THAT HAS STARTED. Those money
// boxes are read-only and carry a button onto the one rate-change process,
// which records the change, puts it in effect on the date it is given and tells
// Finance, the National Account Manager and the executives. An editor that
// wrote the column itself would be a second door, and a second door is where a
// change goes missing.
//
// ⛔ BUT "HAS STARTED" IS THE QUESTION, NOT "DOES A ROW EXIST". That was the
// defect: the moment a building carried a matching line the money went
// read-only, so a line that was merely SCHEDULED — set up, agreed, not yet
// worked a single night — showed its agreed sub payment as a locked 0.00 and
// sent an account manager off to file a rate-change proposal to finish setting
// up a building nobody had cleaned. Setting a rate for the first time is not a
// rate CHANGE. The line now carries its own status and start date and the
// screen asks THOSE — the same predicate the database applies.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { errorMessage } from '../../shared/errors';
import { ServiceLineCard } from './service-line-card';
import { RateChangeModal } from '../account-management/components/rate-change-modal';
import { mayChangeTheLiveBuilding, whyLocked } from './lib/lifecycle';
import { StopServiceLineDialog } from './stop-service-line-dialog';
import { createSubForBuilding, newSubKey } from './sub-onboarding';
import { AlreadyOnFileNotice, alreadyOnFileFrom, type AlreadyOnFileError } from '../subcontractors/already-on-file';
import { toNumOrNull } from '../../shared/coerce';
import {
  emptyServiceLine,
  isLiveOnTheBuilding,
  isMeaningfulLine,
  isOnTheBuilding,
  lineMonthlySub,
  serviceLineToPatch,
  serviceLineTotals,
  type ServiceLineForm,
} from './lib/service-lines';
import type { NewAccountSop } from '../../shared/types';

const money = (v: number | null) =>
  v == null ? '—' : `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ServiceLinesEditor({
  row,
  initialLines,
  onSaved,
  disabled,
}: {
  row: NewAccountSop;
  initialLines: ServiceLineForm[];
  // A line is written on the BUILDING, not through the card's patch door, so
  // nothing the card reads refreshes on its own afterwards. This is that
  // refresh.
  onSaved?: () => void;
  disabled: boolean;
}) {
  // ⛔ The gate is the CARD's state, not the building's. Admin finishes onboarding
  // after go-live all the time; the card stops writing to the live building when
  // the card itself is completed, cancelled or archived (Ben, 2026-09-09).
  const mayWrite = mayChangeTheLiveBuilding(row);
  const lockedBecause = whyLocked(row);
  const isLive = Boolean(row.facility_id);
  const [lines, setLines] = useState<ServiceLineForm[]>(
    initialLines.length > 0 ? initialLines : [emptyServiceLine()],
  );
  const [saving, setSaving] = useState(false);
  const [rateTarget, setRateTarget] = useState<
    {
      assignment_id: string;
      field: 'billing_rate' | 'sub_rate_monthly' | 'sub_hourly_rate';
      label: string;
      current: number | null;
      /** Items 831/758: the figure is per hour. */
      perHour: boolean;
    } | null
  >(null);
  // Stopping a LIVE line is its own act with its own date — it does not happen as
  // a side effect of saving the card.
  const [stopTarget, setStopTarget] = useState<
    { assignment_id: string; label: string; startedOn: string | null } | null
  >(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // "Already on file?" (Ben, 2026-10-28). Every new sub is checked BEFORE any line
  // is written, so a refusal leaves the building untouched; the subs already made
  // are remembered so the second press never makes one twice.
  const madeSubsRef = useRef(new Map<string, string>());
  const addAnywayRef = useRef(new Set<string>());
  const [onFile, setOnFile] = useState<{ key: string; found: AlreadyOnFileError } | null>(null);

  // Resync when the drawer switches to a different card. Keyed on sop_id, not
  // on the row object: re-syncing on every row change would throw away what
  // the person is mid-way through typing every time a Realtime payload lands.
  useEffect(() => {
    setLines(initialLines.length > 0 ? initialLines : [emptyServiceLine()]);
    setErr(null);
    setNote(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.sop_id]);

  // ⛔ NO SECOND FETCH OF THE BUILDING'S LINES. This component used to load them
  // itself and match each card line to one by service role and service type —
  // which is how it came to know only that a row EXISTED. The card's window
  // serves the building's own arrangements now, each carrying its id, its
  // status and its start date, so there is nothing to match and nothing to
  // fetch: the line in hand IS the arrangement.

  const totals = useMemo(() => serviceLineTotals(lines), [lines]);
  const savedTotals = useMemo(() => serviceLineTotals(initialLines), [initialLines]);
  // Compared as the FORM holds them, not as they serialize. serviceLinesToJson
  // used to do this and went with the card's jsonb column on 2026-09-18; the
  // form shape is the better basis anyway, because it also notices a change to
  // a new sub's phone or email — which is not part of the line's patch but is
  // sent to createSubForBuilding, so a save is still owed.
  const dirty = useMemo(
    () => JSON.stringify(lines) !== JSON.stringify(initialLines),
    [lines, initialLines],
  );

  function setLine<K extends keyof ServiceLineForm>(i: number, key: K, value: ServiceLineForm[K]) {
    setLines((prev) => prev.map((ln, idx) => (idx === i ? { ...ln, [key]: value } : ln)));
  }

  async function save() {
    setSaving(true);
    setErr(null);
    setNote(null);
    setOnFile(null);
    try {
      const kept = lines.filter(isMeaningfulLine);

      // ⛔ ONE DOOR, ONE LINE AT A TIME. save_service_line() identifies the
      // arrangement by its id when the building already carries it and creates
      // one when it does not; it decides for itself whether a money field is a
      // setup or a rate change, and it refuses PER FIELD with its own sentence
      // rather than throwing away the rest of the save.
      if (!row.facility_id) {
        throw new Error(
          'This card has no building yet, so there is nowhere to record a service line. Save the card first.',
        );
      }
      const refusals: string[] = [];
      let createdCount = 0;
      let subsCreated = 0;
      const subFor = new Map<ServiceLineForm, string>();
      for (const line of kept) {
        if (line.sub_mode !== 'new' || line.service_provider_id || line.contractor_name.trim().length === 0) continue;
        const key = newSubKey(line.contractor_name, line.contractor_phone);
        let providerId = madeSubsRef.current.get(key);
        if (!providerId) {
          try {
            const made = await createSubForBuilding({
              name: line.contractor_name,
              legalBusinessName: line.contractor_legal_name.trim() || null,
              phone: line.contractor_phone,
              email: line.contractor_email,
              marketRegion: row.market_region ?? null,
              addAnyway: addAnywayRef.current.has(key),
            });
            providerId = made.providerId;
            madeSubsRef.current.set(key, providerId);
            subsCreated += 1;
          } catch (e) {
            const found = alreadyOnFileFrom(e);
            if (found) {
              setOnFile({ key, found });
              return;
            }
            throw e;
          }
        }
        subFor.set(line, providerId);
      }
      for (const line of kept) {
        // ⛔ A "NEW SUB" LINE MINTS THE SUB BEFORE THE LINE IS WRITTEN.
        //
        // This editor took the typed name, put it in worker_name and stopped —
        // no service_providers row, no partner_onboarding record for Antonela,
        // and provider_id left NULL, so the building carried a name nobody
        // could pay and the sub never reached HR's board. The create modal has
        // always done this correctly (#8, 2026-05-29); the editor that replaced
        // its card-side twin did not inherit it, and the second editor removed
        // in this run was the only place on the card that still could.
        //
        // Same one canonical path, so a sub minted here is indistinguishable
        // from one minted at intake. Minted FIRST and linked in the same patch:
        // if save_service_line refuses, HR has a Pending sub with no building,
        // which is a visible loose end. The reverse — a line naming a sub that
        // does not exist — is the silent one.
        const minted = subFor.get(line);
        const toWrite = minted ? { ...line, service_provider_id: minted } : line;
        const { data, error } = await supabase.rpc('save_service_line', {
          p_facility_id: row.facility_id,
          p_patch: serviceLineToPatch(toWrite),
          p_assignment_id: line.assignment_id || null,
          p_sop_id: row.sop_id,
        });
        if (error) throw error;
        const res = (data ?? {}) as {
          created?: boolean;
          refused?: Record<string, string>;
        };
        if (res.created) createdCount += 1;
        for (const sentence of Object.values(res.refused ?? {})) refusals.push(sentence);
      }

      // ⛔ The refusals carry the DATABASE's own sentence, never a second copy
      // of the rule written here. Two copies of a sentence come to disagree
      // exactly like two copies of a fact.
      const uniqueRefusals = Array.from(new Set(refusals));
      const subsNote =
        subsCreated === 0
          ? ''
          : ` ${subsCreated} new sub${subsCreated === 1 ? '' : 's'} sent to HR for setup.`;
      setNote(
        (createdCount === 0
          ? `Saved. ${kept.length} line${kept.length === 1 ? '' : 's'} on this building.`
          : `Saved. ${createdCount} line${createdCount === 1 ? '' : 's'} started on the building.`) +
          subsNote,
      );
      if (uniqueRefusals.length > 0) setErr(uniqueRefusals.join(' '));
      onSaved?.();

    } catch (e) {
      setErr(errorMessage(e, 'Could not save the service lines.'));
    } finally {
      setSaving(false);
    }
  }

  const rateHint =
    'This line is live on the building, so its money moves through the one rate-change ' +
    'process — it takes effect on the date you give it and Finance, the National Account ' +
    'Manager and the executives are told.';

  return (
    <div className="grid gap-3 rounded-md border border-tcs-border bg-tcs-bg/40 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-tcs-text">
          What we do at this building ({lines.filter(isMeaningfulLine).length})
        </p>
        <p className="text-xs text-tcs-text-secondary">
          Client <span className="font-mono text-tcs-text">{money(totals.client)}</span>
          {' · '}Sub <span className="font-mono text-tcs-text">{money(totals.sub)}</span>
          <span className="ml-1">/mo</span>
        </p>
      </div>

      {lockedBecause && (
        <p className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-[11px] text-tcs-text-secondary">
          {lockedBecause}
        </p>
      )}

      <p className="text-[11px] text-tcs-text-secondary">
        {isLive
          ? 'This building is live. Adding a line starts it on the building; removing one closes it out with an end date — nothing is deleted.'
          : 'A nightly crew and a day porter at the same building are two entries here, not two cards.'}
      </p>

      {lines.map((line, i) => {
        // ⛔ TWO DIFFERENT QUESTIONS, AND CONFLATING THEM WAS THE BUG.
        //   onBuilding — the building carries this line at all. Decides whether
        //     removing it is a literal removal or a STOP with a last night.
        //   started    — it is Active and its first night has passed. Decides
        //     whether touching its money is a rate CHANGE.
        // A Scheduled line is on the building and has not started: it is still
        // being set up, so its money is editable and it is stopped, not deleted.
        const onBuilding = isOnTheBuilding(line);
        const started = isLiveOnTheBuilding(line);
        return (
          <ServiceLineCard
            key={i}
            index={i}
            line={line}
            onField={(k, v) => setLine(i, k, v)}
            onRemove={() => {
              // ⛔ A line that is LIVE on the building is not removed by deleting it
              // from a list — it is STOPPED, on a night somebody names. A line that
              // is not live yet has nothing downstream, so removal is literal.
              if (onBuilding) {
                setStopTarget({
                  assignment_id: line.assignment_id,
                  label: `${line.service_label || 'this line'} at ${row.facility_name ?? 'the building'}`,
                  startedOn: line.start_date || null,
                });
                return;
              }
              setLines((prev) => prev.filter((_, idx) => idx !== i));
            }}
            canRemove={lines.length > 1}
            disabled={disabled || saving || !mayWrite}
            rateLocked={started || !mayWrite}
            rateLockedHint={started ? rateHint : undefined}
            removeLabel={onBuilding ? 'Stop this line' : 'Remove'}
            onChangeRate={
              started
                ? (field) =>
                    setRateTarget({
                      assignment_id: line.assignment_id,
                      field,
                      label:
                        field === 'billing_rate'
                          ? line.billing_method === 'Hourly'
                            ? 'client hourly rate'
                            : 'client price'
                          : field === 'sub_hourly_rate'
                            ? 'sub hourly rate'
                            : 'sub payment',
                      current:
                        field === 'billing_rate'
                          ? toNumOrNull(line.client_rate)
                          : field === 'sub_hourly_rate'
                            ? toNumOrNull(line.sub_hourly_rate)
                            : lineMonthlySub(line),
                      perHour:
                        field === 'sub_hourly_rate' ||
                        (field === 'billing_rate' && line.billing_method === 'Hourly'),
                    })
                : undefined
            }
          />
        );
      })}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setLines((prev) => [...prev, emptyServiceLine()])}
          disabled={disabled || saving || !mayWrite}
          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-md border border-dashed border-tcs-border bg-tcs-raised px-3 py-2 text-sm font-medium text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> Add another kind of work at this building
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={disabled || saving || !dirty || !mayWrite}
          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-md border border-tcs-inverse bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink disabled:opacity-50"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? 'Saving…' : 'Save service lines'}
        </button>
      </div>

      {dirty && (savedTotals.client != null || savedTotals.sub != null) && (
        <p className="text-[11px] text-tcs-text-secondary">
          Saved now: client {money(savedTotals.client)} · sub {money(savedTotals.sub)} /mo.
        </p>
      )}
      {err && <p className="text-xs text-rag-red">{err}</p>}
      {onFile && (
        <AlreadyOnFileNotice
          found={onFile.found}
          busy={saving}
          openInNewTab
          instead="If it's them, pick them from the sub list on that line instead of adding a new sub."
          onAddAnyway={() => {
            addAnywayRef.current.add(onFile.key);
            setOnFile(null);
            void save();
          }}
        />
      )}
      {note && !err && <p className="text-xs text-tcs-text-secondary">{note}</p>}

      <StopServiceLineDialog
        open={stopTarget !== null}
        assignmentId={stopTarget?.assignment_id ?? ''}
        label={stopTarget?.label ?? 'this line'}
        startedOn={stopTarget?.startedOn ?? null}
        onClose={() => setStopTarget(null)}
        onStopped={(r) => {
          setNote(
            r.scheduled
              ? `Booked. The line keeps running and stops on ${r.lastNight}.`
              : r.known
                ? `Stopped as of ${r.lastNight}. Finance and the account manager have been told.`
                : 'Stopped. The last night is not on file yet — it is flagged for Finance.',
          );
          onSaved?.();
        }}
      />

      <RateChangeModal
        open={rateTarget !== null}
        onClose={() => setRateTarget(null)}
        assignment_id={rateTarget?.assignment_id ?? ''}
        field_name={rateTarget?.field ?? 'billing_rate'}
        field_label={rateTarget?.label ?? ''}
        current_value={rateTarget?.current ?? null}
        per_hour={rateTarget?.perHour ?? false}
        onRecorded={() => {
          onSaved?.();
        }}
      />
    </div>
  );
}
